import { createContext, useContext, useState, useEffect } from 'react';
import useApi from '../../shared/hooks/useApi';

export const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
    const { apiGet } = useApi();
    
    // Simulation settings state
    const [settings, setSettings] = useState({});
    const [originalSettings, setOriginalSettings] = useState({});
    const [settingsValues, setSettingsValues] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState({});
    
    const [isLoadingSettings, setIsLoadingSettings] = useState(false);
    const [settingsError, setSettingsError] = useState(null);

    // Fetch simulation settings on mount
    const fetchSettings = async () => {
        try {
            setIsLoadingSettings(true);
            setSettingsError(null);
            
            const response = await apiGet("UX_HTTP_API", `/esid-25-006/get-simulation-settings`);
            
            setSettings(response.settings);
            setOriginalSettings(response.settings);

            // Initialize values from Default value
            const initialValues = {};
            Object.entries(response.settings).forEach(([key, setting]) => {
                const defaultValue = setting['Default value'];
                // Handle null/undefined values for select components
                if (defaultValue === null || defaultValue === undefined) {
                    // For select components, use empty string or first available option
                    if (setting['Type']?.toLowerCase() === 'select button' || setting['Type']?.toLowerCase() === 'drop-down') {
                        const selections = setting['selections'];
                        if (selections && Array.isArray(selections) && selections.length > 0) {
                            initialValues[key] = selections[0];
                        } else {
                            initialValues[key] = '';
                        }
                    } else {
                        initialValues[key] = '';
                    }
                } else {
                    initialValues[key] = defaultValue;
                }
            });
            setSettingsValues(initialValues);
        } catch (error) {
            console.error("Error fetching settings:", error);
            setSettingsError(error.message || "Failed to fetch settings");
        } finally {
            setIsLoadingSettings(false);
        }
    };

    // Fetch settings on mount
    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper function to update a single setting value
    const updateSettingValue = (key, value) => {
        setSettingsValues(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Helper function to reset settings to original values
    const resetSettings = () => {
        const initialValues = {};
        Object.entries(originalSettings).forEach(([key, setting]) => {
            const defaultValue = setting['Default value'];
            if (defaultValue === null || defaultValue === undefined) {
                if (setting['Type']?.toLowerCase() === 'select button' || setting['Type']?.toLowerCase() === 'drop-down') {
                    const selections = setting['selections'];
                    if (selections && Array.isArray(selections) && selections.length > 0) {
                        initialValues[key] = selections[0];
                    } else {
                        initialValues[key] = '';
                    }
                } else {
                    initialValues[key] = '';
                }
            } else {
                initialValues[key] = defaultValue;
            }
        });
        setSettingsValues(initialValues);
        setUploadedFiles({});
        
    };

    // Helper function to update uploaded file
    const updateUploadedFile = (type, fileInfo) => {
        if (fileInfo) {
            setUploadedFiles(prev => ({ ...prev, [type]: fileInfo }));
        } else {
            setUploadedFiles(prev => {
                const newFiles = { ...prev };
                delete newFiles[type];
                return newFiles;
            });
        }
    };

    const contextValue = {
        // Settings data
        settings,
        originalSettings,
        settingsValues,
        setSettingsValues,
        
        // Uploaded files
        uploadedFiles,
        setUploadedFiles,
        updateUploadedFile,
        
        // Loading & error states
        isLoadingSettings,
        settingsError,
        
        // Helper functions
        fetchSettings,
        updateSettingValue,
        resetSettings
    };

    return (
        <SettingsContext.Provider value={contextValue}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};